// Editing a task group (S112): the creation draft rebuilt from a stored group, and the
// `PUT /api/task-groups/{id}` body back. The task type, the workflow and the source kind are
// the group's for good (the API refuses another kind; a newer workflow version is an upgrade),
// so the edit walks only the source, secrets and review steps.
import type { Schemas } from "../../api/client";
import type { TaskGroup } from "../model";
import { CADENCE_PRESETS, type Cadence, cadenceMinutes } from "./cadence";
import { type Draft, EMPTY_DRAFT, type MarketplaceWorkflow, networkBody, sourceConfig, type StepKey } from "./draft";
import { EMPTY_QUERY, fromJql } from "./jql";
import { wireRepositories } from "./repositories";

export const EDIT_STEPS = ["source", "access", "review"] as const satisfies readonly StepKey[];

const text = (v: unknown): string => (typeof v === "string" ? v : "");

function cadenceOf(minutes: number): Cadence {
  const preset = CADENCE_PRESETS.find((p) => p === minutes);
  return preset ? { preset, custom: "" } : { preset: "custom", custom: String(minutes) };
}

/**
 * The workflow the group is pinned to, in the shape the wizard's steps read (its name, version
 * number and requirements): the pinned version may be older than the marketplace's latest.
 */
export function pinnedWorkflow(group: TaskGroup, name: string, version: number, requirements: unknown[]): MarketplaceWorkflow {
  return {
    id: "",
    slug: "",
    name,
    description: "",
    task_type_id: group.task_type_id,
    stats: { usage_count: 0, rating_count: 0, review_count: 0, rating: null, average_cost: null, average_results: null },
    version: { id: group.workflow_version_id, version, changelog: "", approval_steps: 0, prompt_steps: 0, published_at: 0, requires_vm: false, requirements },
  } as MarketplaceWorkflow;
}

/** The draft a group's settings make: a Jira query the builder cannot express stays raw. */
export function draftFromGroup(group: TaskGroup, workflow: MarketplaceWorkflow): Draft {
  const c = group.source_config;
  const jql = text(c.jql);
  const query = group.source === "jira" ? fromJql(jql) : null;
  return {
    ...EMPTY_DRAFT,
    taskTypeId: group.task_type_id,
    workflow,
    source: group.source,
    credentialId: group.source_credential_id ?? "",
    github: {
      repo: text(c.repo),
      labels: Array.isArray(c.labels) ? c.labels.filter((l): l is string => typeof l === "string").join(", ") : "",
      assignee: text(c.assignee),
      includeClosed: c.state === "all",
    },
    jira: {
      ...(query ? { raw: false, query } : { raw: group.source === "jira", query: EMPTY_QUERY }),
      text: jql,
      includeSubtasks: c.include_subtasks === true,
    },
    asana: { project: text(c.project), section: text(c.section) },
    cadence: cadenceOf(group.poll_minutes),
    repositories: group.repositories.map((r) => ({ repo: r.repo, branch: r.default_branch ?? "" })),
    bindings: Object.fromEntries(Object.entries(group.bindings).map(([k, v]) => [Number(k), v])),
    name: group.name,
    maxParallel: group.max_parallel_tasks,
    requiresVpn: group.requires_vpn,
    networkListId: group.network_list_id ?? "",
    shareVmNetwork: group.share_vm_network,
  };
}

export type UpdateBody = Omit<Schemas["UpdateTaskGroupBody"], "repositories"> & {
  repositories: ReturnType<typeof wireRepositories>;
  network_list_id: string | null;
  share_vm_network: boolean;
};

/** `PUT /api/task-groups/{id}`'s body: every setting; the source kind is the group's own. */
export function updateBody(d: Draft): UpdateBody {
  const polled = d.source !== "manual";
  return {
    name: d.name.trim(),
    source: d.source,
    source_config: sourceConfig(d),
    source_credential_id: polled && d.credentialId ? d.credentialId : null,
    ...(polled ? { poll_minutes: cadenceMinutes(d.cadence) ?? 5 } : {}),
    max_parallel_tasks: d.maxParallel,
    requires_vpn: d.requiresVpn,
    ...networkBody(d),
    bindings: Object.fromEntries(Object.entries(d.bindings).filter(([, v]) => v !== "")),
    repositories: wireRepositories(d.repositories),
  };
}
